import { useState, useEffect } from "react";
import {
  Users, DollarSign, Target, User, Percent,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import BrokerDoletasWidget from "./BrokerDoletasWidget";

var STATUS_LABELS = {
  qualificacao: "Qualificação",
  simulacao: "Simulação / Cotação",
  reuniao: "Reunião",
  proposta: "Proposta Enviada",
  negociacao: "Negociação",
  fechado: "Fechamento",
};

var STATUS_STYLES = {
  qualificacao: { bg: "bg-blue-50", border: "border-blue-100", bar: "bg-[#3B82F6]", badge: "bg-[#3B82F6]" },
  simulacao:    { bg: "bg-amber-50", border: "border-amber-100", bar: "bg-amber-500", badge: "bg-amber-500" },
  reuniao:      { bg: "bg-purple-50", border: "border-purple-100", bar: "bg-purple-500", badge: "bg-purple-500" },
  proposta:     { bg: "bg-orange-50", border: "border-orange-100", bar: "bg-[#F26A4B]", badge: "bg-[#F26A4B]" },
  negociacao:   { bg: "bg-cyan-50", border: "border-cyan-100", bar: "bg-cyan-500", badge: "bg-cyan-500" },
  fechado:      { bg: "bg-emerald-50", border: "border-emerald-100", bar: "bg-emerald-500", badge: "bg-emerald-500" },
};

function formatValue(v) {
  if (v == null || v === 0) return "R$ 0";
  if (v >= 1000) return "R$ " + (v / 1000).toFixed(1) + "k";
  return "R$ " + v.toFixed(0);
}

function FunnelStage(props) {
  var f = props.item;
  var maxCount = props.maxCount;
  var style = STATUS_STYLES[f.status] || STATUS_STYLES.qualificacao;
  var label = STATUS_LABELS[f.status] || f.status;
  var pct = maxCount > 0 ? Math.round((f.count / maxCount) * 100) : 0;

  return (
    <div className={"flex items-center gap-3 p-3 rounded-xl border " + style.bg + " " + style.border}>
      <div className={"w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 " + style.badge}>
        <span className="text-white font-bold text-sm">{f.count}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0A2F3D]">{label}</p>
        <div className={"h-1.5 rounded-full overflow-hidden mt-1 " + style.bg}>
          <div className={"h-full rounded-full " + style.bar} style={{ width: pct + "%" }} />
        </div>
      </div>
      <span className="text-xs text-gray-500 flex-shrink-0">{formatValue(f.total_value)}</span>
    </div>
  );
}

export default function CorretorDashboard() {
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];
  var navigate = useNavigate();

  useEffect(function() {
    async function fetchData() {
      try {
        var res = await api.get("/dashboard/corretor");
        setData(res.data);
      } catch (err) {
        console.error("Erro ao carregar dashboard:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="corretor-dashboard">
        <div className="w-8 h-8 border-3 border-[#F26A4B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  var d = data || {};
  var funnel = d.funnel || [];
  var maxFunnelCount = 1;
  for (var i = 0; i < funnel.length; i++) {
    if (funnel[i].count > maxFunnelCount) maxFunnelCount = funnel[i].count;
  }

  var salesTarget = 10;
  var commTarget = 15000;
  var clientTarget = 8;
  var salesPct = Math.min(Math.round(((d.my_sales || 0) / salesTarget) * 100), 100);
  var commPct = Math.min(Math.round(((d.my_commissions || 0) / commTarget) * 100), 100);
  var clientPct = Math.min(Math.round(((d.my_clients || 0) / clientTarget) * 100), 100);

  return (
    <div data-testid="corretor-dashboard">
      <BrokerDoletasWidget />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div onClick={function() { navigate("/leads"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border-l-4 border-l-[#F26A4B] cursor-pointer hover:shadow-md hover:ring-1 hover:ring-[#F26A4B]/20 transition-all" data-testid="kpi-card-meus-clientes">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-[#F26A4B]/10 rounded-lg lg:rounded-xl">
              <Users className="w-5 h-5 lg:w-6 lg:h-6 text-[#F26A4B]" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-meus-clientes">{d.my_clients || 0}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Meus Clientes</p>
        </div>
        <div onClick={function() { navigate("/comissoes"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md hover:ring-1 hover:ring-emerald-500/20 transition-all" data-testid="kpi-card-minha-comissao">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-emerald-500/10 rounded-lg lg:rounded-xl">
              <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-500" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-minha-comissao">{formatValue(d.my_commissions)}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Minha Comissão</p>
        </div>
        <div onClick={function() { navigate("/leads"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border-l-4 border-l-[#3B82F6] cursor-pointer hover:shadow-md hover:ring-1 hover:ring-[#3B82F6]/20 transition-all" data-testid="kpi-card-vendas">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-[#3B82F6]/10 rounded-lg lg:rounded-xl">
              <Target className="w-5 h-5 lg:w-6 lg:h-6 text-[#3B82F6]" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-vendas-mes">{d.my_sales || 0}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Vendas este Mês</p>
        </div>
        <div onClick={function() { navigate("/leads"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md hover:ring-1 hover:ring-amber-500/20 transition-all" data-testid="kpi-card-conversao">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-amber-500/10 rounded-lg lg:rounded-xl">
              <Percent className="w-5 h-5 lg:w-6 lg:h-6 text-amber-500" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-conversao">{d.conversion_rate || 0}%</p>
          <p className="text-gray-500 text-xs lg:text-sm">Taxa de Conversão</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div className="lg:col-span-2 bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <div>
              <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Meu Funil de Vendas</h3>
              <p className="text-xs lg:text-sm text-gray-500">Seus leads em andamento</p>
            </div>
          </div>
          {funnel.length > 0 ? (
            <div className="space-y-3">
              {funnel.map(function(f) {
                return <FunnelStage key={f.status} item={f} maxCount={maxFunnelCount} />;
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">
              Nenhum lead no funil ainda. Cadastre seu primeiro lead!
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm">
          <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D] mb-4 lg:mb-6">Minhas Metas</h3>
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs lg:text-sm text-gray-600">Vendas Mensais</span>
                <span className="text-xs font-medium text-[#0A2F3D]">{d.my_sales || 0}/{salesTarget}</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA]" style={{ width: salesPct + "%" }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {(d.my_sales || 0) >= salesTarget ? "Meta atingida!" : "Faltam " + (salesTarget - (d.my_sales || 0)) + " para a meta"}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs lg:text-sm text-gray-600">Comissão Mensal</span>
                <span className="text-xs font-medium text-[#0A2F3D]">{formatValue(d.my_commissions)} / {formatValue(commTarget)}</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: commPct + "%" }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{commPct}% da meta atingida</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs lg:text-sm text-gray-600">Clientes Ativos</span>
                <span className="text-xs font-medium text-[#0A2F3D]">{d.my_clients || 0}/{clientTarget}</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#F26A4B] to-[#f8917a]" style={{ width: clientPct + "%" }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {(d.my_clients || 0) >= clientTarget ? "Meta atingida!" : "Faltam " + (clientTarget - (d.my_clients || 0)) + " para a meta"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
